<template>
    <div
        ref="container"
        class="base-map"
    >
        <div
            v-if="!loadedMap"
            class="base-map__loader"
        />
    </div>
</template>

<script setup>
import mapboxgl from 'mapbox-gl';
import '~/node_modules/mapbox-gl/dist/mapbox-gl.css';

const config = useRuntimeConfig();
const { t } = useI18n();

mapboxgl.accessToken = config.public.mapboxApiKey;

const props = defineProps({
    markers: {
        type: Array,
        default: () => []
    },
    mapStyle: {
        type: String,
        default: ''
    }
});

const container = ref(null);
const loadedMap = ref(null);

function markersToGeoJson(markers) {
    if (!markers || markers.length === 0) {
        return null;
    }

    return {
        type: 'FeatureCollection',
        features: markers.map((marker) => {
            return {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [marker.longitude, marker.latitude]
                },
                properties: {
                    title: marker.markerTitle,
                    description: marker.markerDescription,
                    link: marker.markerLink,
                    externalLink: marker.markerLinkExternal
                }
            };
        })
    };
}

function inferZoomlevelFromCoordinates(markers) {
    if (!markers || markers.length === 0) {
        return 0;
    }

    if (markers.length === 1) {
        return -7;
    }

    const latitudes = markers.map((marker) => marker.latitude);
    const longitudes = markers.map((marker) => marker.longitude);

    const minLat = Math.min(...latitudes);
    const maxLat = Math.max(...latitudes);
    const minLng = Math.min(...longitudes);
    const maxLng = Math.max(...longitudes);

    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;

    const latZoom = Math.floor(Math.log2(360 / 360 * latDiff));
    const lngZoom = Math.floor(Math.log2(180 / 180 * lngDiff));

    return Math.min(latZoom, lngZoom);
}

function calculateBoundingBox(geojson) {
    const bounds = new mapboxgl.LngLatBounds();

    for (const feature of geojson.features) {
        bounds.extend(feature.geometry.coordinates);

        // calculate paddingAmount based on zoom level
        const paddingAmount = 1 * Math.pow(2, inferZoomlevelFromCoordinates(props.markers));

        // add top padding
        bounds.extend([
            Number(feature.geometry.coordinates[0]) + paddingAmount,
            Number(feature.geometry.coordinates[1]) + paddingAmount
        ]);
        // add bottom padding
        bounds.extend([
            Number(feature.geometry.coordinates[0]) - paddingAmount,
            Number(feature.geometry.coordinates[1]) - paddingAmount
        ]);
    }

    return bounds;
}

onMounted(() => {
    if (!container.value || props.markers.length === 0 || !config.public.mapboxApiKey) {
        return;
    }

    const map = new mapboxgl.Map({
        container: container.value,
        style: props.mapStyle,
        scrollZoom: false,
        bounds: calculateBoundingBox(markersToGeoJson(props.markers))
    });

    loadedMap.value = map;

    // add navigation control (zoom buttons) to the map
    map.addControl(new mapboxgl.NavigationControl());

    // add markers to map
    for (const feature of markersToGeoJson(props.markers).features) {
        // create a HTML element for each feature
        const el = document.createElement('div');
        el.className = 'base-map__marker';
        el.innerHTML = feature.properties.title;

        const linkElement = () => {
            if (feature.properties.link?.[0]?.uri) {
                return `<a class="base-arrow-link" href="${feature.properties.link[0].uri}">${t('view')} <span class="base-arrow-link__arrow" /></a>`;
            } else if (feature.properties.externalLink) {
                return `<a class="base-arrow-link" href="${feature.properties.externalLink}" target="_blank" rel="noopener noreferrer">${t('view')} <span class="base-arrow-link__arrow" /></a>`;
            }

            return '';
        };

        // construct popup
        const popupHTML = `
            <h3>${feature.properties.title}</h3>
            ${feature.properties.description ? `<p>${feature.properties.description}</p>` : ''}
            ${linkElement()}
        `;

        // make a marker for each feature and add to the map
        if (feature.properties.description || feature.properties.link?.[0]?.uri) {
            new mapboxgl.Marker(el)
                .setLngLat(feature.geometry.coordinates)
                .setPopup(
                    new mapboxgl.Popup(
                        {
                            offset: 25,
                            className: 'base-map__popup',
                            focusAfterOpen: false,
                            maxWidth: '400px'
                        }
                    ) // add popups
                        .setHTML(
                            popupHTML
                        )
                )
                .addTo(map);
        } else {
            new mapboxgl.Marker(el)
                .setLngLat(feature.geometry.coordinates)
                .addTo(map);
        }
    }
});
</script>

<style lang="less">
.base-map {
    width: 100%;
    height: 100%;

}

.base-map__marker {
    background-color: white;
    padding: .25rem .5rem;
    // cursor: pointer;
    font-size: .875rem;
}

.base-map__popup {
    .mapboxgl-popup-tip {
        display: none;
    }

    .mapboxgl-popup-content {
        padding: var(--spacing-md);
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
        border-radius: 0;

        background: var(--color-oasis-green);
        color: var(--color-lightest);
    }

    .mapboxgl-popup-close-button {
        font-size: var(--font-size-4xl);
        line-height: .25rem;
        width: 2.5rem;
        height: 2.5rem;
        padding: .2rem .55rem .5rem;
        color: var(--color-lightest);
    }

    h3 {
        font-family: var(--font-family-primary);
        font-weight: var(--font-weight-primary-normal);
        line-height: 1;
        text-transform: uppercase;
        font-size: var(--font-size-2xl);
    }

    p {
        font-size: var(--font-size-lg);
        margin-bottom: 0;
    }
}

.base-map__loader {
    height: 100%;
    width: 100%;
    background: var(--color-gray-300);
}


// HACKY IMPORT OF BASE ARROW LINK STYLES
.base-map__popup .base-arrow-link {
    .typography-text();
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-primary-normal);
    text-transform: uppercase;
    align-self: flex-end;

    .link-clean();

    display: inline-flex;
    align-items: center;
    line-height: 1;
    gap: var(--spacing-2xs);

    &[href] {
        text-decoration: none;
        color: currentColor;

        &:hover,
        &:visited {
            color: currentColor;
        }
    }

    &:focus {
        outline: none;
    }

    a[href]:active &,
    &:active {
        opacity: .6;
    }

    @media @q-lg-min {
        font-size: var(--font-size-md);
    }
}

.base-arrow-link__arrow {
    height: 1px;
    width: 1.5em;
    margin-bottom: .2em;
    display: block;
    background-color: currentColor;
    position: relative;
    transition: all .5s cubic-bezier(.4, 0, .2, 1);

    &:before, &:after {
        content: '';
        width: 1px;
        height: 1px;
        display: block;
        background-color: currentColor;
        position: absolute;
        top: 0;
        right: 0;
        transform-origin: center right;
        transition: width .5s cubic-bezier(.5, 0, .2, 1), opacity .5s cubic-bezier(.5, 0, .2, 1);
        opacity: 0;
    }

    &:before {
        transform: rotate(45deg);
    }

    &:after {
        transform: rotate(-45deg);
    }
}
</style>
